import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { useGetSite } from 'src/features/sites/api/useGetSite';
import SiteForm from 'src/features/sites/components/SiteForm';
import { siteFormSchema, SiteFormSchemaValues } from 'src/features/sites/utils/formSchema';
import { Site } from 'src/features/sites/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { generalMessages } from 'src/utils/messages';

interface SiteFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedSite?: Site;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: SiteFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function SiteFormModal<TData>({
    selectedSite,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: SiteFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { parkingId } = useParking();

    // We are in edit mode if we have a selected site
    // otherwise it means we are creating a new site

    const { data: site } = useGetSite({
        siteId: selectedSite?.id ?? '',
        parkingId,
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedSite?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(
        () =>
            site
                ? {
                      ...site,
                      service_provider: site.service_provider.id,
                  }
                : {
                      coordinates: {
                          type: 'Point',
                          coordinates: [0, 0],
                      },
                      translations: [],
                      floors: 0,
                      is_24_7: false,
                      service_provider: '',
                  },
        [site],
    );

    const form = useForm<SiteFormSchemaValues>({
        resolver: zodResolver(siteFormSchema),
        defaultValues,
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        if (isModalOpen) {
            form.reset(defaultValues);
        }
    }, [isModalOpen, defaultValues, form]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: SiteFormSchemaValues) => {
            return mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [mutate, form, onError],
    );
    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={className}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <SiteForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedSite && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default SiteFormModal;
